<script setup lang="ts">
/**
 * The left column: who you are, where you go, what you are in.
 *
 * The layout owns where this sits and how tall it may be: its wrapper is the
 * sticky, scrolling column, and this just stacks its cards inside. That split
 * is deliberate — a class put on `<SideRail>` lands on *this* root through
 * attribute fallthrough, which is how `hidden`/`xl:block` once fought `flex`
 * over `display` and silently collapsed the gap between these cards to zero.
 *
 * Every figure on it is fetched, not written: `followerCount` and
 * `followingCount` come from the profile, `memberCount` from the group. The
 * design's left column also offered Bookmarks, Events and Media — none of
 * which exist — and a "favourite groups" list, which is the groups you are in
 * with no way to mark one as a favourite. What is here is the groups you are
 * actually in, most recently active first, which is the honest version of the
 * same shortcut.
 *
 * Hidden below `xl`, where the nav row in the top bar covers the same ground.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import type { GroupSummary, PageProfile } from '@talkvibe/shared';

import Avatar from './Avatar.vue';
import NavIcon, { type IconName } from './NavIcon.vue';
import NotificationBadge from './NotificationBadge.vue';
import SoonBadge from './SoonBadge.vue';
import { chatService } from '../services/chat.ts';
import { pageService } from '../services/pages.ts';
import { useAuthStore } from '../stores/auth.ts';
import { useChatStore } from '../stores/chat.ts';
import { useNotificationStore } from '../stores/notification.ts';
import { handle } from '../utils/format.ts';
import { useI18n } from '../i18n/index.ts';

const auth = useAuthStore();
const chat = useChatStore();
const notifications = useNotificationStore();
const route = useRoute();
const { t, plural, formatNumber } = useI18n();

const myPages = ref<PageProfile[]>([]);

/** Groups are conversations of type `group`; there is no separate resource. */
/**
 * The groups you are in, from their own endpoint.
 *
 * Not `chat.conversations`: the inbox leaves post groups out on purpose, so reading it
 * here would have made joining a post group look like leaving it — the shortcut would
 * simply stop listing it.
 */
const groups = ref<GroupSummary[]>([]);

onMounted(async () => {
  try {
    groups.value = (await chatService.myGroups()).items.slice(0, 5);
  } catch {
    // A shortcut list is a garnish on the rail; an empty one is what a failure looks
    // like here, and the Groups tab above still works.
  }
});

const links = computed<{ to: string; label: string; icon: IconName; badge?: number }[]>(() => [
  { to: '/', label: t('nav.newsFeed'), icon: 'home' },
  { to: '/friends', label: t('nav.friends'), icon: 'friends' },
  { to: '/messages', label: t('nav.messages'), icon: 'messages', badge: chat.totalUnread },
  {
    to: '/notifications',
    label: t('nav.notifications'),
    icon: 'notifications',
    badge: notifications.unreadCount,
  },
  { to: '/discover', label: t('nav.discover'), icon: 'discover' },
  { to: '/settings', label: t('nav.settings'), icon: 'settings' },
  // Bookmarks joined this list the day saving a post started working. It was in
  // `soonLinks` below, which is where a shortcut sits while there is nothing on
  // the other end of it.
  { to: '/bookmarks', label: t('nav.bookmarks'), icon: 'saved' },
]);

/**
 * The rest of the design's shortcut list. Nothing holds an event or files media
 * yet, so these sit here in the shape they will take, disabled until there is
 * something on the other end of them.
 */
const soonLinks = computed<{ label: string; icon: IconName }[]>(() => [
  { label: t('nav.events'), icon: 'calendar' },
  { label: t('nav.media'), icon: 'image' },
]);

const isActive = (to: string): boolean =>
  route.path === to || (to !== '/' && route.path.startsWith(to));

async function loadPages(): Promise<void> {
  if (!auth.isAuthenticated) {
    myPages.value = [];
    return;
  }
  try {
    myPages.value = await pageService.mine();
  } catch {
    // A rail that cannot list your pages is still a working rail.
    myPages.value = [];
  }
}

onMounted(loadPages);
watch(() => auth.isAuthenticated, loadPages);
</script>

<template>
  <aside
    v-if="auth.user"
    class="flex flex-col gap-space-lg py-space-lg"
    :aria-label="t('nav.quickNav')"
  >
    <!-- Who you are. Both counts are the profile's own, never estimated. -->
    <section class="rounded-xl bg-surface-container-lowest p-space-md shadow-sm">
      <RouterLink :to="`/@${auth.user.username}`" class="flex items-center gap-3">
        <span class="relative shrink-0">
          <Avatar
            :name="auth.user.displayName"
            :src="auth.user.avatarUrl"
            :asset="auth.user.avatarAsset"
            :user-id="auth.user.id"
            size="md"
          />
          <span
            class="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-tertiary-container ring-2 ring-surface-container-lowest"
            aria-hidden="true"
          />
        </span>
        <span class="min-w-0 flex-1">
          <span class="block truncate text-headline-sm text-on-surface">
            {{ auth.user.displayName }}
          </span>
          <span class="block truncate text-body-sm text-on-surface-variant">
            {{ handle(auth.user.username) }}
          </span>
        </span>
      </RouterLink>

      <p
        v-if="auth.user.bio"
        class="mt-3 truncate rounded-lg bg-surface-container-low px-2.5 py-1.5 text-body-sm text-on-surface"
      >
        {{ auth.user.bio }}
      </p>

      <dl class="mt-3 grid grid-cols-2 gap-2 pt-3">
        <RouterLink
          :to="`/@${auth.user.username}`"
          class="rounded-lg px-2 py-1.5 text-center transition-colors hover:bg-surface-container-low"
        >
          <dd class="block text-headline-sm tabular-nums text-on-surface">
            {{ formatNumber(auth.user.followerCount) }}
          </dd>
          <dt class="text-label-sm text-on-surface-variant">
            {{ t('nav.followersLabel') }}
          </dt>
        </RouterLink>
        <RouterLink
          to="/friends"
          class="rounded-lg px-2 py-1.5 text-center transition-colors hover:bg-surface-container-low"
        >
          <dd class="block text-headline-sm tabular-nums text-on-surface">
            {{ formatNumber(auth.user.followingCount) }}
          </dd>
          <dt class="text-label-sm text-on-surface-variant">{{ t('nav.followingLabel') }}</dt>
        </RouterLink>
      </dl>
    </section>

    <!-- Where you go. -->
    <nav class="rounded-xl bg-surface-container-lowest p-3 shadow-sm" :aria-label="t('nav.quickNav')">
      <span class="block px-3 py-1.5 text-label-sm uppercase tracking-wider text-outline">
        {{ t('nav.quickNav') }}
      </span>
      <ul class="mt-1 space-y-0.5">
        <li v-for="link in links" :key="link.to">
          <RouterLink
            :to="link.to"
            class="flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-label-md transition-colors"
            :class="
              isActive(link.to)
                ? 'bg-surface-container text-primary'
                : 'text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface'
            "
            :aria-current="isActive(link.to) ? 'page' : undefined"
          >
            <span class="flex min-w-0 items-center gap-3">
              <NavIcon :name="link.icon" :filled="isActive(link.to)" class="h-5 w-5 shrink-0" />
              <span class="truncate">{{ link.label }}</span>
            </span>
            <NotificationBadge v-if="link.badge" :count="link.badge" />
          </RouterLink>
        </li>

        <li v-for="link in soonLinks" :key="link.label">
          <span
            class="flex cursor-not-allowed items-center justify-between gap-3 rounded-lg px-3 py-2 text-label-md text-on-surface-variant opacity-60"
            :title="t('common.notReadyYet')"
            aria-disabled="true"
          >
            <span class="flex min-w-0 items-center gap-3">
              <NavIcon :name="link.icon" class="h-5 w-5 shrink-0" />
              <span class="truncate">{{ link.label }}</span>
            </span>
            <SoonBadge />
          </span>
        </li>
      </ul>
    </nav>

    <!-- Groups you are in, with their real membership. -->
    <section class="rounded-xl bg-surface-container-lowest p-3 shadow-sm">
      <div class="mb-1 flex items-center justify-between px-3 py-1.5">
        <span class="text-label-sm uppercase tracking-wider text-outline">
          {{ t('nav.favouriteGroups') }}
        </span>
        <RouterLink to="/messages" class="text-label-sm text-primary hover:underline">
          {{ t('nav.manage') }}
        </RouterLink>
      </div>

      <p v-if="groups.length === 0" class="px-3 py-1.5 text-body-sm text-on-surface-variant">
        {{ t('nav.noGroups') }}
      </p>

      <ul v-else class="space-y-1">
        <li v-for="group in groups" :key="group.id">
          <RouterLink
            :to="`/messages/${group.id}`"
            class="group flex items-center gap-2.5 rounded-lg p-2 transition-colors hover:bg-surface-container-low"
          >
            <span
              class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-container-high text-primary"
            >
              <NavIcon name="friends" class="h-4 w-4" />
            </span>
            <span class="min-w-0 flex-1">
              <span class="block truncate text-label-md text-on-surface group-hover:text-primary">
                {{ group.name }}
              </span>
              <span class="block text-label-sm text-outline">
                {{ plural('nav.members', group.memberCount) }}
              </span>
            </span>
          </RouterLink>
        </li>
      </ul>

      <!-- The same shape the pages section below uses, and the reason the
           inbox no longer carries this: a group starts on the feed. -->
      <RouterLink
        to="/groups/new"
        class="mt-0.5 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-label-md text-primary transition-colors hover:bg-surface-container-low"
      >
        <NavIcon name="person-add" class="h-5 w-5 shrink-0" />
        <span class="truncate">{{ t('group.create') }}</span>
      </RouterLink>
    </section>

    <!-- Pages you own. -->
    <section class="rounded-xl bg-surface-container-lowest p-3 shadow-sm">
      <span class="block px-3 py-1.5 text-label-sm uppercase tracking-wider text-outline">
        {{ t('pages.yours') }}
      </span>
      <ul class="mt-1 space-y-0.5">
        <li v-for="page in myPages" :key="page.slug">
          <RouterLink
            :to="`/p/${page.slug}`"
            class="flex items-center gap-3 rounded-lg px-3 py-2 text-label-md text-on-surface-variant transition-colors hover:bg-surface-container-low hover:text-on-surface"
          >
            <NavIcon name="saved" class="h-5 w-5 shrink-0" />
            <span class="truncate">{{ page.name }}</span>
          </RouterLink>
        </li>
      </ul>
      <RouterLink
        to="/pages/new"
        class="mt-0.5 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-label-md text-primary transition-colors hover:bg-surface-container-low"
      >
        <NavIcon name="more" class="h-5 w-5 shrink-0" />
        <span class="truncate">{{ t('pages.create') }}</span>
      </RouterLink>
    </section>

    <p class="px-3 text-label-sm leading-relaxed text-outline">{{ t('nav.tagline') }}</p>

  </aside>
</template>
